// =====================================================
// PÁGINA: Perfil do Corretor
// =====================================================

import { useState, useEffect } from 'react'
import { User, Mail, Phone, MapPin, Calendar, Edit3, Save, X } from 'lucide-react'
import { supabase } from '../services/supabase'
import { useAuth } from '../contexts/AuthContext'

interface Corretor {
    id: string
    nome: string
    email: string
    telefone?: string
    creci?: string
    endereco?: string
    cidade?: string
    estado?: string
    cep?: string
    created_at: string
    updated_at: string
}

export default function Perfil() {
    const { user } = useAuth()
    const [corretor, setCorretor] = useState<Corretor | null>(null)
    const [loading, setLoading] = useState(true)
    const [editing, setEditing] = useState(false)
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState('')
    const [success, setSuccess] = useState('')

    const [formData, setFormData] = useState({
        nome: '',
        email: '',
        telefone: '',
        creci: '',
        endereco: '',
        cidade: '',
        estado: '',
        cep: ''
    })

    useEffect(() => {
        if (user) {
            loadPerfil()
        }
    }, [user])

    const loadPerfil = async () => {
        try {
            setLoading(true)
            setError('')

            if (!user) {
                throw new Error('Usuário não autenticado')
            }

            const { data: corretorData, error: corretorError } = await supabase
                .from('corretores')
                .select('*')
                .eq('user_id', user.id)
                .single()

            if (corretorError) {
                console.error('❌ Erro ao carregar perfil:', corretorError)
                throw corretorError
            }

            setCorretor(corretorData)
            setFormData({
                nome: corretorData.nome || '',
                email: corretorData.email || '',
                telefone: corretorData.telefone || '',
                creci: corretorData.creci || '',
                endereco: corretorData.endereco || '',
                cidade: corretorData.cidade || '',
                estado: corretorData.estado || '',
                cep: corretorData.cep || ''
            })

            console.log('✅ Perfil carregado:', corretorData)

        } catch (err) {
            console.error('❌ Erro ao carregar perfil:', err)
            setError('Erro ao carregar perfil')
        } finally {
            setLoading(false)
        }
    }

    const handleEdit = () => {
        setEditing(true)
        setError('')
        setSuccess('')
    }

    const handleCancel = () => {
        setEditing(false)
        if (corretor) {
            setFormData({
                nome: corretor.nome || '',
                email: corretor.email || '',
                telefone: corretor.telefone || '',
                creci: corretor.creci || '',
                endereco: corretor.endereco || '',
                cidade: corretor.cidade || '',
                estado: corretor.estado || '',
                cep: corretor.cep || ''
            })
        }
    }

    const handleSave = async () => {
        try {
            setSaving(true)
            setError('')
            setSuccess('')

            const { error: updateError } = await supabase
                .from('corretores')
                .update({
                    nome: formData.nome,
                    telefone: formData.telefone || null,
                    creci: formData.creci || null,
                    endereco: formData.endereco || null,
                    cidade: formData.cidade || null,
                    estado: formData.estado || null,
                    cep: formData.cep || null,
                    updated_at: new Date().toISOString()
                })
                .eq('id', corretor?.id)

            if (updateError) {
                console.error('❌ Erro ao atualizar perfil:', updateError)
                throw updateError
            }

            setSuccess('Perfil atualizado com sucesso!')
            setEditing(false)
            await loadPerfil()

        } catch (err) {
            console.error('❌ Erro ao salvar perfil:', err)
            setError('Erro ao atualizar perfil')
        } finally {
            setSaving(false)
        }
    }

    const handleInputChange = (field: string, value: string) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }))
    }

    if (loading) {
        return (
            <div className="p-4 lg:p-6">
                <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
            </div>
        )
    }

    if (error && !corretor) {
        return (
            <div className="p-4 lg:p-6">
                <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
                    <p className="text-red-600 mb-4">{error}</p>
                    <button
                        onClick={loadPerfil}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                    >
                        Tentar novamente
                    </button>
                </div>
            </div>
        )
    }

    return (
        <div className="p-4 lg:p-6">
            <div className="max-w-4xl mx-auto">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-2">Perfil do Corretor</h1>
                    <p className="text-gray-600">Gerencie suas informações pessoais e profissionais</p>
                </div>

                {/* Success Message */}
                {success && (
                    <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-center">
                        <div className="w-5 h-5 bg-green-500 rounded-full mr-3"></div>
                        <span className="text-green-700">{success}</span>
                    </div>
                )}

                {/* Error Message */}
                {error && (
                    <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center">
                        <div className="w-5 h-5 bg-red-500 rounded-full mr-3"></div>
                        <span className="text-red-700">{error}</span>
                    </div>
                )}

                {/* Profile Card */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    {/* Header do Card */}
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-8 border-b border-gray-200">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-4">
                                <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center">
                                    <User className="w-8 h-8 text-white" />
                                </div>
                                <div>
                                    <h2 className="text-xl font-semibold text-gray-900">
                                        {editing ? formData.nome : corretor?.nome}
                                    </h2>
                                    <p className="text-gray-600">{corretor?.email}</p>
                                </div>
                            </div>
                            <div className="flex space-x-2">
                                {editing ? (
                                    <>
                                        <button
                                            onClick={handleCancel}
                                            className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                                            disabled={saving}
                                        >
                                            <X className="w-5 h-5" />
                                        </button>
                                        <button
                                            onClick={handleSave}
                                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
                                            disabled={saving}
                                        >
                                            {saving ? (
                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                            ) : (
                                                <Save className="w-4 h-4" />
                                            )}
                                            <span>{saving ? 'Salvando...' : 'Salvar'}</span>
                                        </button>
                                    </>
                                ) : (
                                    <button
                                        onClick={handleEdit}
                                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
                                    >
                                        <Edit3 className="w-4 h-4" />
                                        <span>Editar</span>
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Conteúdo do Card */}
                    <div className="p-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Informações Pessoais */}
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-4">Informações Pessoais</h3>
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Nome Completo</label>
                                        {editing ? (
                                            <input
                                                type="text"
                                                value={formData.nome}
                                                onChange={(e) => handleInputChange('nome', e.target.value)}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                required
                                            />
                                        ) : (
                                            <p className="text-gray-900">{corretor?.nome || 'Não informado'}</p>
                                        )}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">E-mail</label>
                                        <p className="text-gray-900 flex items-center">
                                            <Mail className="w-4 h-4 mr-2 text-gray-400" />
                                            {corretor?.email}
                                        </p>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Telefone</label>
                                        {editing ? (
                                            <input
                                                type="tel"
                                                value={formData.telefone}
                                                onChange={(e) => handleInputChange('telefone', e.target.value)}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                placeholder="(11) 99999-9999"
                                            />
                                        ) : (
                                            <p className="text-gray-900 flex items-center">
                                                <Phone className="w-4 h-4 mr-2 text-gray-400" />
                                                {corretor?.telefone || 'Não informado'}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Informações Profissionais */}
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-4">Informações Profissionais</h3>
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">CRECI</label>
                                        {editing ? (
                                            <input
                                                type="text"
                                                value={formData.creci}
                                                onChange={(e) => handleInputChange('creci', e.target.value)}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                placeholder="123456"
                                            />
                                        ) : (
                                            <p className="text-gray-900">{corretor?.creci || 'Não informado'}</p>
                                        )}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Endereço</label>
                                        {editing ? (
                                            <input
                                                type="text"
                                                value={formData.endereco}
                                                onChange={(e) => handleInputChange('endereco', e.target.value)}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                placeholder="Rua, número, bairro"
                                            />
                                        ) : (
                                            <p className="text-gray-900 flex items-center">
                                                <MapPin className="w-4 h-4 mr-2 text-gray-400" />
                                                {corretor?.endereco || 'Não informado'}
                                            </p>
                                        )}
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Cidade</label>
                                            {editing ? (
                                                <input
                                                    type="text"
                                                    value={formData.cidade}
                                                    onChange={(e) => handleInputChange('cidade', e.target.value)}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                    placeholder="São Paulo"
                                                />
                                            ) : (
                                                <p className="text-gray-900">{corretor?.cidade || 'Não informado'}</p>
                                            )}
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
                                            {editing ? (
                                                <input
                                                    type="text"
                                                    value={formData.estado}
                                                    onChange={(e) => handleInputChange('estado', e.target.value)}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                    placeholder="SP"
                                                />
                                            ) : (
                                                <p className="text-gray-900">{corretor?.estado || 'Não informado'}</p>
                                            )}
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">CEP</label>
                                        {editing ? (
                                            <input
                                                type="text"
                                                value={formData.cep}
                                                onChange={(e) => handleInputChange('cep', e.target.value)}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                placeholder="01234-567"
                                            />
                                        ) : (
                                            <p className="text-gray-900">{corretor?.cep || 'Não informado'}</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Informações do Sistema */}
                        <div className="mt-8 pt-6 border-t border-gray-200">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">Informações do Sistema</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="flex items-center text-gray-600">
                                    <Calendar className="w-4 h-4 mr-2" />
                                    <span className="text-sm">Membro desde: {new Date(corretor?.created_at || '').toLocaleDateString('pt-BR')}</span>
                                </div>
                                <div className="flex items-center text-gray-600">
                                    <Calendar className="w-4 h-4 mr-2" />
                                    <span className="text-sm">Última atualização: {new Date(corretor?.updated_at || '').toLocaleDateString('pt-BR')}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}


